import {
    createContext,
    lazy,
    useContext,
    useRef,
    type FC,
    type ReactNode,
    type RefObject,
} from 'react';
// eslint-disable-next-line import/no-unresolved
import { MediaController } from 'media-chrome/react';
import { type MediaController as MediaControllerType } from 'media-chrome';

import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

const Controls = lazy(() =>
    import(/* webpackChunkName: "VideoControls" */ './Controls.tsx').then(module => ({
        default: module.Controls,
    }))
);

const Logo = lazy(() =>
    import(/* webpackChunkName: "VideoLogo" */ './Logo.tsx').then(module => ({
        default: module.Logo,
    }))
);

const Media = lazy(() =>
    import(/* webpackChunkName: "VideoMedia" */ './Media.tsx').then(module => ({
        default: module.Media,
    }))
);

type Props = {
    children: ReactNode;
    ref?: RefObject<HTMLVideoElement | null>;
    forceMute?: boolean;
    dataTn?: string;
    fallback?: ReactNode;
};

type VideoPlayerType = {
    Controls: typeof Controls;
    Logo: typeof Logo;
    Media: typeof Media;
};

type ContextType = {
    playerRef?: RefObject<HTMLVideoElement | null>;
    wrapperRef?: RefObject<MediaControllerType | null>;
    forceMute?: boolean;
    dataTn?: string;
    fallback?: ReactNode;
};

const VideoPlayerContext = createContext<ContextType | null>(null);

export const useVideoPlayer = (): ContextType => {
    const context = useContext(VideoPlayerContext);

    if (!context) {
        throw new Error('useVideoPlayer must be used within a <VideoPlayer />');
    }

    return context;
};

export const VideoPlayer: FC<Props> & VideoPlayerType = ({
    children,
    ref,
    forceMute = true,
    dataTn,
    fallback,
}) => {
    const wrapperRef = useRef<MediaControllerType | null>(null);

    return (
        <VideoPlayerContext.Provider
            value={{
                playerRef: ref,
                forceMute,
                dataTn,
                fallback,
                wrapperRef,
            }}
        >
            {/* // TODO: decide on a fallback for the video player */}
            <ClientSuspense
                fallback={
                    fallback || (
                        <div
                            className={classnames(
                                dibsCss.wFull,
                                dibsCss.hFull,
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.justifyCenter,
                                dibsCss.flexCol
                            )}
                        >
                            <Spinner black size="small" />
                            <span
                                className={classnames(
                                    dibsCss.mtSmall,
                                    dibsCss.sassyFontSubHeaderMediumNormal
                                )}
                            >
                                <FormattedMessage
                                    id="VimeoVideo.loadingVideo"
                                    defaultMessage="Video Loading"
                                />
                            </span>
                        </div>
                    )
                }
            >
                <MediaController
                    ref={wrapperRef}
                    className={classnames(
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.bgTransparent,
                        dibsCss.overflowHidden
                    )}
                >
                    {children}
                </MediaController>
            </ClientSuspense>
        </VideoPlayerContext.Provider>
    );
};

VideoPlayer.Controls = Controls;
VideoPlayer.Logo = Logo;
VideoPlayer.Media = Media;
